import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Printer, Star, ArrowLeft, ChevronDown, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

const SERVICE_LABELS = { ground: "PCP/ACP-G", sfa_emr: "SFA/EMR", wes: "PCP-PS", air_adult: "Air Adult", air_pediatric: "OB/NEO/PEDS" };

export default function QuickReferenceCards({ protocols, favorites }) {
  const [expandedCards, setExpandedCards] = useState({});
  const favoriteProtocols = protocols.filter(p => favorites.some(f => f.protocol_id === p.id));

  const toggleCard = (id) => setExpandedCards(prev => ({ ...prev, [id]: !prev[id] }));

  const handlePrint = () => window.print();

  if (favoriteProtocols.length === 0) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <Link to="/favorites"><Button variant="ghost" size="sm"><ArrowLeft className="w-4 h-4 mr-1" />Back</Button></Link>
          <h1 className="font-heading text-2xl font-bold">Quick Reference Cards</h1>
        </div>
        <div className="text-center py-12 text-muted-foreground">
          <Star className="w-12 h-12 mx-auto mb-4 opacity-40" />
          <p>No favorites yet. Star protocols to generate reference cards.</p>
          <Link to="/"><Button variant="outline" className="mt-4">Browse Protocols</Button></Link>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="quick-reference-cards">
      {/* Header - hidden in print */}
      <div className="flex items-center justify-between no-print">
        <div className="flex items-center gap-3">
          <Link to="/favorites"><Button variant="ghost" size="sm"><ArrowLeft className="w-4 h-4 mr-1" />Favorites</Button></Link>
          <h1 className="font-heading text-2xl font-bold flex items-center gap-2">
            <Printer className="w-6 h-6" />
            Quick Reference Cards
          </h1>
          <Badge variant="outline">{favoriteProtocols.length} protocols</Badge>
        </div>
        <Button onClick={handlePrint} className="gap-2" data-testid="print-reference-cards">
          <Printer className="w-4 h-4" /> Print Cards
        </Button>
      </div>

      {/* Print-only header */}
      <div className="hidden print-only">
        <h1 style={{ fontSize: "18px", fontWeight: "bold", marginBottom: "4px" }}>YEMS Quick Reference Cards</h1>
        <p style={{ fontSize: "11px", color: "#666", marginBottom: "12px" }}>{favoriteProtocols.length} protocols — Printed {new Date().toLocaleDateString()}</p>
      </div>

      {/* Cards grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 print-cards-grid">
        {favoriteProtocols.map(protocol => (
          <div
            key={protocol.id}
            className="border rounded-lg overflow-hidden bg-card print-card"
            data-testid={`ref-card-${protocol.id}`}
          >
            {/* Card header */}
            <div
              className="px-3 py-2 bg-primary/5 border-b cursor-pointer flex items-center justify-between no-print-cursor"
              onClick={() => toggleCard(protocol.id)}
            >
              <div className="min-w-0 flex-1">
                <h3 className="font-semibold text-sm truncate">{protocol.name}</h3>
                <div className="flex items-center gap-1.5 mt-0.5">
                  <Badge variant="outline" className="text-[10px] px-1 py-0 h-4">
                    {SERVICE_LABELS[protocol.service] || protocol.service}
                  </Badge>
                </div>
              </div>
              <span className="no-print">
                {expandedCards[protocol.id] ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
              </span>
            </div>

            {/* Card body — always visible in print, collapsible on screen */}
            <div className={`px-3 py-2 text-xs print-always-show ${expandedCards[protocol.id] ? '' : 'hidden-on-screen'}`}>
              {(protocol.care_levels || []).map((level, li) => (
                <div key={li} className="mb-2 last:mb-0">
                  <div className="font-semibold text-[11px] uppercase tracking-wide text-muted-foreground mb-0.5 border-b border-border/40 pb-0.5">
                    {level.title || level.level}
                  </div>
                  <ul className="space-y-0.5">
                    {(level.interventions || []).map((int, ii) => (
                      <li key={ii}>
                        <span className="text-[11px] leading-tight">{int.text}</span>
                        {int.sub_steps && int.sub_steps.length > 0 && (
                          <ul className="ml-3 mt-0.5 space-y-0">
                            {int.sub_steps.map((sub, si) => (
                              <li key={si} className="text-[10px] text-muted-foreground leading-tight">— {sub}</li>
                            ))}
                          </ul>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}

              {/* Info boxes (warnings/notes) */}
              {protocol.info_boxes && protocol.info_boxes.length > 0 && (
                <div className="mt-2 pt-1 border-t border-dashed border-border/40">
                  {protocol.info_boxes.map((box, bi) => (
                    <div key={bi} className="text-[10px] mt-1">
                      <span className={`font-bold uppercase ${box.type === 'warning' ? 'text-amber-500' : box.type === 'critical' ? 'text-red-500' : 'text-blue-500'}`}>
                        {box.title}:
                      </span>{" "}
                      <span className="text-muted-foreground">{box.content?.substring(0, 200)}{box.content?.length > 200 ? '...' : ''}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
